import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Coins, Sparkles, Gift, TrendingUp, Loader2, ArrowRight } from 'lucide-react';

export default function CoinStorePage() {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    const headers = getAuthHeaders();
    Promise.all([
      fetch(`${API}/coins/packs`).then(r => r.json()),
      fetch(`${API}/coins/balance`, { headers }).then(r => r.ok ? r.json() : { balance_coins: 0 }),
      fetch(`${API}/coins/history?limit=15`, { headers }).then(r => r.ok ? r.json() : { transactions: [] }),
    ]).then(([p, b, h]) => {
      setPacks(p.packs || []);
      setBalance(b.balance_coins || 0);
      setHistory(h.transactions || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [authLoading, isAuthenticated, getAuthHeaders, navigate]);

  const handleBuy = async (pack) => {
    setBuying(pack.pack_id);
    try {
      const res = await fetch(`${API}/coins/checkout`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_id: pack.pack_id }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.success) {
        toast.success(`+${data.coins_credited} coins added`);
        setBalance(data.new_balance);
      } else {
        toast.error(data.detail || 'Purchase failed');
      }
    } catch {
      toast.error('Network error');
    }
    setBuying(null);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-background text-foreground pt-24 pb-16" data-testid="coin-store-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">

        {/* Header + balance */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Coins</h1>
              <p className="text-xs text-muted-foreground">Fuel your favorite creators with tips and gifts.</p>
            </div>
          </div>
          <Card className="px-4 py-2.5 rounded-2xl bg-gradient-to-r from-amber-500/15 to-orange-500/10 border-amber-500/30" data-testid="coin-store-balance">
            <p className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">Balance</p>
            <p className="text-xl font-bold tabular-nums flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-400" />
              {balance.toLocaleString()}
            </p>
          </Card>
        </div>

        {/* Packs grid */}
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Coin Packs</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="coin-store-packs">
          {packs.map(pack => {
            const total = pack.coins + (pack.bonus || 0);
            const perDollar = Math.round(total / pack.price_usd);
            return (
              <Card key={pack.pack_id} className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-card/80 to-card/40 border-border hover:border-amber-500/40 transition-all" data-testid={`pack-${pack.pack_id}`}>
                {pack.bonus > 0 && (
                  <span className="absolute top-2 right-2 text-[9px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    +{pack.bonus} bonus
                  </span>
                )}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <Coins className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold tabular-nums leading-tight">{total.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest">coins</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mb-3">≈ {perDollar} coins per $1</p>
                <Button
                  disabled={buying === pack.pack_id}
                  onClick={() => handleBuy(pack)}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 h-10 text-base font-bold"
                  data-testid={`pack-buy-${pack.pack_id}`}
                >
                  {buying === pack.pack_id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy for $${pack.price_usd.toFixed(2)}`}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* How it works */}
        <Card className="p-5 rounded-2xl bg-gradient-to-br from-violet-500/5 to-card/60 border-violet-500/20 mb-6" data-testid="coin-store-how">
          <h3 className="font-bold mb-2 flex items-center gap-2"><Gift className="w-4 h-4 text-violet-400" /> What can you do with coins?</h3>
          <ul className="text-xs text-muted-foreground space-y-1.5">
            <li>· <b className="text-foreground">Send animated gifts</b> — 🌹 Rose (10c), 🔥 Fire (50c), 💎 Diamond (100c), 👑 Crown (250c), 🚀 Rocket (1,000c)</li>
            <li>· <b className="text-foreground">Support livestreams</b> — gift during live shows to appear on the leaderboard</li>
            <li>· <b className="text-foreground">Tip creators</b> — every gift converts to real money for the artist</li>
          </ul>
        </Card>

        {/* Recent history */}
        {history.length > 0 && (
          <>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet-400" /> Recent Activity</h2>
            <Card className="p-4 rounded-2xl" data-testid="coin-store-history">
              <ul className="divide-y divide-border/50">
                {history.map(tx => {
                  const positive = tx.coins_delta > 0;
                  return (
                    <li key={tx.tx_id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`coin-tx-${tx.tx_id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{tx.type === 'purchase' ? 'Coin pack purchased' : tx.type === 'gift_send' ? 'Gift sent' : tx.type}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                      </div>
                      <p className={`text-sm font-bold tabular-nums ${positive ? 'text-emerald-400' : 'text-orange-400'}`}>
                        {positive ? '+' : ''}{tx.coins_delta}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </>
        )}

        <div className="mt-8 text-center">
          <button onClick={() => navigate('/creator/wallet')} className="text-xs text-violet-400 hover:text-violet-300 inline-flex items-center gap-1" data-testid="coin-store-wallet-link">
            View my wallet <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
